import {
  EntityState,
  createAsyncThunk,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { ThunkExtra } from '@app/store';
import {
  DashboardService,
  GetUserWidgetPostsFeedResponse,
  GetUserWidgetPostsFeedParams,
  GetUserWidgetPostsFeedQuery,
} from '@app/gen/dashboard';
import { DynamicEntity } from '@app/types';
import { CommunityService } from '@app/gen/community';
import { NewsService } from '@app/gen/news';
import { ContentService } from '@app/gen/content';

import {
  createSurveyAnswersAsyncThunk,
  setContentSurveyAnswers,
} from '../../../../content-surveys';
import { DashboardState } from '../../dashboard.state';

const getGroupAvatarUrlList = createAsyncThunk<
  Record<string, string>,
  string[],
  ThunkExtra
>(
  'dashboard/getGroupAvatarUrlList',
  async (
    avatarAttachmentIds,
    { rejectWithValue, getState, extra: { inject } },
  ) => {
    try {
      const contentService = inject<ContentService>(ContentService);
      const avatarPrevAttachments =
        getState().dashboard.feedPostsList.avatars.list;

      const avatarAttachmentUniqueIds = Array.from(
        new Set(avatarAttachmentIds),
      );
      const avatarAttachmentNewIds = avatarAttachmentUniqueIds.filter(
        (id) => !(id in avatarPrevAttachments),
      );

      const filesWithMeta = await Promise.all(
        avatarAttachmentNewIds.map(async (attachmentId: string) => {
          const response = await contentService.getAttachmentUrl({
            params: { attachment_id: attachmentId },
          });

          return { attachmentId, url: response.url };
        }),
      );

      return filesWithMeta.reduce(
        (
          acc: Record<string, string>,
          curr: {
            attachmentId: string;
            url: string;
          },
        ) => {
          acc[curr.attachmentId] = curr.url;
          return acc;
        },
        {},
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getPostsFeed = createAsyncThunk<
  GetUserWidgetPostsFeedResponse,
  {
    query?: GetUserWidgetPostsFeedQuery;
    params: GetUserWidgetPostsFeedParams;
  },
  ThunkExtra
>(
  'dashboard/getPostsFeed',
  async (dto, { rejectWithValue, extra: { inject }, dispatch }) => {
    try {
      const dashboardService = inject(DashboardService);

      const result = await dashboardService.getUserWidgetPostsFeed(dto);
      const groupAttachmentIds = result.feed
        .filter((post) => post.type === 'post')
        .map((post) => post.community.avatar_attachment_id)
        .filter((id): id is string => Boolean(id));

      await dispatch(getGroupAvatarUrlList(groupAttachmentIds));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const markPostAsRead = createAsyncThunk<
  void,
  { communityId: string; postId: string },
  ThunkExtra
>(
  'dashboard/markPostAsRead',
  async ({ communityId, postId }, { rejectWithValue, extra: { inject } }) => {
    try {
      const communityService = inject(CommunityService);

      await communityService.markPostAsRead({
        params: { community_id: communityId, post_id: postId },
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const markNewsAsRead = createAsyncThunk<
  void,
  { newsId: string },
  ThunkExtra
>(
  'dashboard/markNewsAsRead',
  async ({ newsId }, { rejectWithValue, extra: { inject } }) => {
    try {
      const newsService = inject(NewsService);

      await newsService.markNewsAsRead({
        params: { news_id: newsId },
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const sendSurveyAnswers = createSurveyAnswersAsyncThunk(
  'dashboard/sendSurveyAnswers',
);

export const feedListReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(getPostsFeed.pending, (state, { meta }) => {
    state.feedPostsList.error = null;
    const offset = meta.arg?.query?.offset || 0;

    if (offset === 0) {
      state.feedPostsList.status = 'loading';
    }
  });
  builder.addCase(getPostsFeed.fulfilled, (state, { payload, meta }) => {
    state.feedPostsList.error = null;
    state.feedPostsList.status = 'complete';
    const offset = meta.arg?.query?.offset || 0;

    if (offset === 0) {
      state.feedPostsList.posts = payload.feed;
    } else {
      state.feedPostsList.posts = [
        ...state.feedPostsList.posts,
        ...payload.feed,
      ];
    }
    state.feedPostsList.total = payload?.total ?? 0;
  });
  builder.addCase(getPostsFeed.rejected, (state, { payload, error }) => {
    if (error?.message === 'Aborted') state.feedPostsList.status = 'loading';
    else state.feedPostsList.status = 'failed';

    state.feedPostsList.error =
      payload ||
      ({
        info: (error && error.message) || 'Что-то пошло не так',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getGroupAvatarUrlList.pending, (state) => {
    state.feedPostsList.avatars.status = 'loading';
  });
  builder.addCase(getGroupAvatarUrlList.fulfilled, (state, action) => {
    state.feedPostsList.avatars.status = 'complete';
    state.feedPostsList.avatars.list = Object.assign(
      {},
      state.feedPostsList.avatars.list,
      action.payload,
    );
  });
  builder.addCase(getGroupAvatarUrlList.rejected, (state) => {
    state.feedPostsList.avatars.status = 'failed';
  });

  builder.addCase(sendSurveyAnswers.fulfilled, (state, action) => {
    const postId = action.meta.arg.values.entityId;
    const surveyId = action.meta.arg.values.params.content_survey_id;

    state.feedPostsList.posts = setContentSurveyAnswers(
      state.feedPostsList.posts,
      postId,
      surveyId,
      action.payload,
    );
  });
};
